import React from 'react';
import {
    CareTeamMemberDetails,
    UpsertCareTeamMember,
} from '../../../primary-record-types/apiTypes/medicalProfileService';
import { AnimateLoadedContent } from '../../v2BaseComponents/Loader';
import { InsuranceFormValue, ProviderFormValue, SupportFormValue } from './types';
import CareTeamTypeConditionalRenderContainer from './CareTeamTypeConditionalRenderContainer';
import { useGoBack } from '../../../utils/appNavigationNames';
import useRecordEvent from '../../../hooks/useRecordEvent';
import { Form } from '../../v2BaseComponents/Form';
import { useCareTeamFormHelpers } from './useCareTeamFormHelpers';
import { CareTeamMemberSource } from '../../../utils/careTeamUtils';

export type CareTeamActionButtonFormOwnProps = {
    careTeamMemberDetails?: CareTeamMemberDetails;
    onSubmit: (createBody: UpsertCareTeamMember) => void;
    isSubmitting: boolean;
    source: CareTeamMemberSource;
    careTeamMemberId?: string;
    shouldRecordAddCareTeamMember?: boolean;
};

export type CareTeamFormProps = CareTeamActionButtonFormOwnProps;

const CareTeamActionButtonForm: React.FC<CareTeamFormProps> = ({
    careTeamMemberDetails,
    onSubmit,
    isSubmitting,
    source,
    careTeamMemberId,
    shouldRecordAddCareTeamMember,
}) => {
    const goBack = useGoBack();
    const recordEvent = useRecordEvent();
    const { getInitialValues, getSubmitValues, renderFields } = useCareTeamFormHelpers({
        initialCareTeamMemberDetails: careTeamMemberDetails || null,
        isDisabled: isSubmitting,
    });

    return (
        <AnimateLoadedContent isLoading={isSubmitting}>
            {() => (
                <CareTeamTypeConditionalRenderContainer
                    initialCareTeamMemberDetails={careTeamMemberDetails || null}
                    isDisabled={isSubmitting}
                    areUpdatesDisabled={!!careTeamMemberDetails}
                    renderNullCareTeamSelectInActionButtonContainer
                >
                    {(careTeamMemberType) => (
                        <Form
                            actionContainerSubmit
                            initialValues={getInitialValues(careTeamMemberType)}
                            onCancel={goBack}
                            cancelButtonVariant="secondary"
                            onSubmit={(values) => {
                                onSubmit(
                                    getSubmitValues(
                                        careTeamMemberType,
                                        values as InsuranceFormValue | ProviderFormValue | SupportFormValue,
                                    ),
                                );
                                if (shouldRecordAddCareTeamMember) {
                                    recordEvent('addCareTeamMember');
                                }
                            }}
                        >
                            {({ values }) =>
                                renderFields({
                                    values: values as InsuranceFormValue | ProviderFormValue | SupportFormValue,
                                    isDisabled: isSubmitting,
                                    careTeamMemberType,
                                    source,
                                    careTeamMemberId,
                                })
                            }
                        </Form>
                    )}
                </CareTeamTypeConditionalRenderContainer>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(CareTeamActionButtonForm);
